import { useContext, useMemo } from 'react';

import MultiSelect, { MultiSelectOption } from '@/components/common/MultiSelect';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { ProgramApiResponse, ProgramNames, programNameToId } from '@/models/Program';

export function ProgramSelect({
  selectedPrograms,
  onProgramsChange,
}: {
  selectedPrograms: ProgramNames[];
  onProgramsChange: (programIds: ProgramNames[]) => void;
}) {
  const { allPrograms } = useContext(ConfigContext);

  const selectedProgramValues: string[] = useMemo(
    () =>
      selectedPrograms?.map(
        (p: ProgramNames) =>
          // p to string
          allPrograms.find((ap) => programNameToId[ap.programName] === p)?.id.toString() || ''
      ) || [],

    [allPrograms, selectedPrograms]
  );

  const enumNumberValues = <T extends object>(e: T) =>
    Object.values(e).filter((v): v is number => typeof v === 'number');

  const programNameValues = new Set<number>(enumNumberValues(ProgramNames));

  const isProgramNames = (n: number): n is ProgramNames =>
    Number.isFinite(n) && programNameValues.has(n);

  const handleProgramsChange = (programIds: string[]) => {
    const programNames: ProgramNames[] = programIds.map((id) => Number(id)).filter(isProgramNames);

    onProgramsChange(programNames);
  };

  return (
    <MultiSelect
      id='select-program'
      options={allPrograms?.map(
        (p: ProgramApiResponse) =>
          ({
            key: p.programName,
            name: p.programName,
            value: p.id.toString(),
          })
      )}
      label='Programs'
      selectedValues={selectedProgramValues}
      setSelectedValues={handleProgramsChange}
      required
    />
  );
}
